// REFUSALS — one component, every arm, and the server's sentence whenever there is one.
//
// A typed refusal (`kind: "v2Error"`) always carries `error.copy`, so that is what the owner reads:
// the code goes on the element and the text is rendered untouched. The client adds no sentence of its
// own and never re-words one — the same table serves iOS (R112, "One table, every client").
//
// THE OTHER ARMS HAVE NO SERVER SENTENCE AND CANNOT HAVE ONE. `contractMismatch`, `network`,
// `badResponse` and `token` are decided by the generated client about a response it could not read or
// never received; by definition no sentence came back with them. iOS has the same hole and fills it with
// strings it holds locally, and so does this client now: each arm renders a sentence from `heldCopy.ts`,
// word for word as Tyler approved it for the website on 2026-09-29 (WEB-COPY-1, "approve all sentences..",
// items 7, 15 and 16). They are held HERE, not in the server's one table, because two of them tell the
// owner to refresh the page — an act only a web client has, which R112 keeps out of a table the app reads.
//
// NO CODES, STATUSES OR VERSIONS ON THE PAGE (WEB-PARITY-1B, SW-7). Each arm used to end in a line for us — "NOT_ENTITLED
// · 403", the mismatch reason and both contract versions, the transport's own problem text. They stay on the element as
// `data-*` and leave the page. Where the app shows a status to an owner it does it in a sentence: LetterService.swift:259,
// "Something went wrong on our end (500)… contact us and quote that number", its line for a failure ON OUR SIDE. So that
// sentence stands where the code line stood on a 5xx, and nowhere else: a 4xx is a refusal whose own sentence already
// gives the reason, and calling it "something went wrong" is the app's own F10 defect (LetterService.swift:249-256: "an
// ordinary limit, phrased as a crash").

//
// A FAULT ON OUR SIDE IS NOT A REFUSAL (WALK-FIX-1, R236). "We can't go on with this." is the heading of
// a REFUSAL: the server decided, and its sentence says why. Over a 5xx — our fault, and a retry may well work — it read
// as a verdict: Tyler, 2026-10-05, saw it on the Letter step over the server's own "that's on us" and "(500)", when
// staging's storage was missing. A screen whose app shows its own heading for a failure it can retry passes it as
// `faultHeading`, and a 5xx draws that instead; a 4xx keeps the refusal's heading. The Letter step passes the app's "We
// couldn't finish this letter" (GenerateView.swift:975, the heading the app puts over every failed generation).

import type { ReactNode } from "react";
import type { V2Failure } from "@contract";
import { CopyNote, HeldLine } from "./copy";
import { heldTemplate } from "./heldCopy";

export function Refusal({ failure, context, faultHeading }: { failure: V2Failure; context: string; faultHeading?: ReactNode }) {
  return (
    <div className="refusal" data-refusal-kind={failure.kind} role="alert">
      <p className="code">{context}</p>
      <RefusalBody failure={failure} faultHeading={faultHeading} />
    </div>
  );
}

export function RefusalBody({ failure, faultHeading }: { failure: V2Failure; faultHeading?: ReactNode }) {
  switch (failure.kind) {
    case "v2Error":
      return (
        <div data-error-code={failure.error.error.code} data-error-status={failure.error.error.status}>
          {failure.error.error.status >= 500 && faultHeading !== undefined ? faultHeading : <h1>We can't go on with this.</h1>}
          <CopyNote copy={failure.error.error.copy} tone="stop" />
          {failure.error.error.status >= 500 && (
            <p className="faint" data-held-copy="refusal.serverError">
              {heldTemplate("refusal.serverError", { status: failure.error.error.status })}
            </p>
          )}
        </div>
      );
    case "contractMismatch":
      return (
        <div
          data-mismatch-reason={failure.reason}
          data-expected-version={failure.expected.version}
          data-received-version={failure.received.version}
        >
          <h1>This page is out of step with the service.</h1>
          <HeldLine id="transport.contractMismatch" />
        </div>
      );
    case "network":
    case "badResponse":
      return (
        <div data-transport-problem={failure.problem}>
          <h1>We couldn't reach the service.</h1>
          <HeldLine id="transport.unreachable" />
        </div>
      );
    case "token":
      return (
        <div data-token-which={failure.which} data-transport-problem={failure.problem}>
          <h1>We couldn't confirm who's asking.</h1>
          {/* Which token failed is for us — the two have different fixes — and the owner's sentence is the same. */}
          <HeldLine id="transport.token" />
        </div>
      );
  }
}
